import React, {useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useQuery} from 'react-query';
import {Box, LinearProgress, Paper, Typography} from '@mui/material';
import {DataGridPro} from '@mui/x-data-grid-pro';
import Canvas from '../../navigation/canvas/Canvas';
import ErrorPopup from '../../common/ErrorPopup';
import PenHealthService from '../../../apis/PenHealthService';
import {buildBusinessColumns, businessGroupingColDef, columnGroupingModel, getTreeDataPath} from './columns';

export const PEN_HEALTH_SUMMARY_QUERY = 'getPenHealthSummary';

export const usePenHealthSummary = (handleError) => useQuery(PEN_HEALTH_SUMMARY_QUERY, PenHealthService.getBusinessSummary, {
  onError: handleError,
  retry: false
});

export const describeError = (error) => `${error.message}` +
  `${error.response?.data ? ': ' + JSON.stringify(error.response.data) : ''}`;

const farmKey = (row) => row.multi_site_owner_id ?? `single:${row.business_id}`;

const orderByFarm = (rows) => {
  const farmHigh = new Map();
  rows.forEach((row) => farmHigh.set(farmKey(row), (farmHigh.get(farmKey(row)) || 0) + (row.high_count || 0)));
  return [...rows].sort((a, b) => (farmHigh.get(farmKey(b)) - farmHigh.get(farmKey(a)))
    || String(farmKey(a)).localeCompare(String(farmKey(b)))
    || (b.is_multi_site_parent - a.is_multi_site_parent)
    || ((b.high_count || 0) - (a.high_count || 0)));
};

const PenHealth = () => {
  const history = useHistory();
  const [errDisplayText, setErrDisplayText] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);

  const handleError = (error) => {
    setErrDisplayText(describeError(error));
    setErrorOpen(true);
  };

  const summary = usePenHealthSummary(handleError);
  const columns = useMemo(buildBusinessColumns, []);
  const rows = useMemo(() => orderByFarm(summary.data || []), [summary.data]);
  const staleCount = rows.filter((r) => r.is_stale).length;
  const farmCount = new Set(rows.map(farmKey)).size;

  return (
    <Box>
      <Canvas appName='nGEST' title='Pen Health'>
        <Typography variant='body2' sx={{mb: '1rem', color: 'text.secondary'}}>
          {farmCount} farms ({rows.length} businesses with cows); multi-site farms list their sites under the parent. High means definitely misconfigured, or low for 7+ days; issue counts
          are bold when they include a High. {staleCount > 0 && `${staleCount} evaluated on a stale herd snapshot. `}
          Click a business for its pens.
        </Typography>
        <Paper sx={{height: '70vh'}}>
          <DataGridPro
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            treeData
            getTreeDataPath={getTreeDataPath}
            groupingColDef={businessGroupingColDef}
            defaultGroupingExpansionDepth={-1}
            rows={rows}
            getRowId={(row) => row.business_id}
            loading={summary.isLoading}
            slots={{loadingOverlay: LinearProgress}}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            onRowClick={({row}) => row.business_id && history.push(`/pen-health/${row.business_id}`)}
            sx={{'& .MuiDataGrid-row': {cursor: 'pointer'}}}
          />
        </Paper>
      </Canvas>
      <ErrorPopup open={errorOpen} setOpen={setErrorOpen} displayText={errDisplayText}/>
    </Box>
  );
};

export default PenHealth;
